import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Camera, Save, Trash2, IdCard, ArrowRight, UserCircle, AlertTriangle, MessageCircle } from "lucide-react";
import ImageCropper from "@/components/ImageCropper";
import { BRAZIL_STATES } from "@/lib/brazil";
import SupporterFields from "@/components/SupporterFields";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const REGION_BY_UF = {
  AC:"Norte",AL:"Nordeste",AP:"Norte",AM:"Norte",BA:"Nordeste",CE:"Nordeste",DF:"Centro-Oeste",
  ES:"Sudeste",GO:"Centro-Oeste",MA:"Nordeste",MT:"Centro-Oeste",MS:"Centro-Oeste",MG:"Sudeste",
  PA:"Norte",PB:"Nordeste",PR:"Sul",PE:"Nordeste",PI:"Nordeste",RJ:"Sudeste",RN:"Nordeste",
  RS:"Sul",RO:"Norte",RR:"Norte",SC:"Sul",SP:"Sudeste",SE:"Nordeste",TO:"Norte",
};

const DOC_TYPES = [
  { value: "cpf", label: "CPF" },
  { value: "rg", label: "RG" },
  { value: "cn", label: "Cert. Nascimento" },
  { value: "outros", label: "Outros" },
];

const CONDITION_SUGGESTIONS = [
  "Autismo (TEA)", "TDAH", "Dislexia", "Discalculia", "Disgrafia",
  "Síndrome de Down", "Síndrome de Tourette", "Altas Habilidades / Superdotação",
  "Transtorno de Ansiedade", "Transtorno Obsessivo-Compulsivo (TOC)",
  "Paralisia Cerebral", "Deficiência Intelectual",
];

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "IC";
}

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", phone: "", state: "", city: "",
    child_name: "", child_condition: "", child_birth_date: "",
    document_type: "", document_number: "",
  });
  const [supporter, setSupporter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        state: user.state || "",
        city: user.city || "",
        child_name: user.child_name || "",
        child_condition: user.child_condition || "",
        child_birth_date: user.child_birth_date || "",
        document_type: user.document_type || "",
        document_number: user.document_number || "",
      });
      setSupporter(user.supporter || null);
    }
  }, [user]);

  const region = useMemo(() => (form.state ? REGION_BY_UF[form.state] : ""), [form.state]);
  const photoUrl = user?.profile_photo_path ? `${API}/files/${user.profile_photo_path}` : null;

  const upd = (k) => (e) => setForm((p) => ({ ...p, [k]: typeof e === "string" ? e : e.target.value }));

  const pickFile = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Envie uma imagem."); return; }
    setPendingFile(f);
    setCropOpen(true);
  };

  const onCropped = async (blob) => {
    setCropOpen(false); setPendingFile(null);
    if (!blob) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      await api.post("/me/profile-photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto atualizada.");
      await refresh();
    } catch (_) { toast.error("Falha ao enviar foto."); }
    finally { setUploading(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remover sua foto de perfil?")) return;
    try { await api.delete("/me/profile-photo"); toast.success("Foto removida."); await refresh(); }
    catch (_) { toast.error("Falha ao remover."); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (region) payload.region = region;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (supporter) payload.supporter = supporter;
      await api.put("/me/profile", payload);
      // If user removed supporter role, clear it via dedicated endpoint
      if (!supporter && user?.supporter) {
        await api.delete("/me/supporter");
      }
      toast.success("Perfil atualizado com sucesso!");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar.");
    } finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50">
        <div className="container-cuidar py-10">
          <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--secondary))]">Sua conta</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black flex items-center gap-3">
            <UserCircle className="h-8 w-8 text-[hsl(var(--primary))]" /> Meu perfil
          </h1>
          <p className="mt-3 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Mantenha seus dados e da criança sempre atualizados — eles alimentam a sua carteirinha e o atendimento da nossa equipe.
          </p>
        </div>
      </section>

      <section className="container-cuidar py-10 grid gap-8 lg:grid-cols-[320px_1fr]">
        {/* Photo + card shortcut */}
        <div className="space-y-6">
          <div className="surface p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Foto</div>
            <div className="mt-4 flex flex-col items-center gap-4">
              <Avatar className="h-32 w-32 border-4 border-white shadow-lg" data-testid="profile-avatar-large">
                {photoUrl && <AvatarImage src={photoUrl} alt={user.name} />}
                <AvatarFallback className="bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-heading font-black text-2xl">
                  {initials(user.name)}
                </AvatarFallback>
              </Avatar>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="profile-photo-input" />
              <div className="flex gap-2 w-full">
                <Button onClick={pickFile} disabled={uploading}
                  className="flex-1 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                  data-testid="profile-photo-change-btn">
                  <Camera className="h-4 w-4 mr-1.5" /> {uploading ? "Enviando…" : (photoUrl ? "Trocar" : "Enviar")}
                </Button>
                {photoUrl && (
                  <Button variant="outline" onClick={removePhoto}
                    className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10"
                    data-testid="profile-photo-remove-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-center text-[hsl(var(--muted-foreground))]">
                Ajuste corte, zoom e rotação antes de salvar — aparece na comunidade e no menu.
              </p>
            </div>
          </div>

          <MemberCardPhotoCard />

          <div className="surface p-6 bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/20">
            <div className="flex items-center gap-2 font-heading font-bold">
              <IdCard className="h-5 w-5 text-[hsl(var(--primary))]" /> Carteirinha de apoio
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Gere um PDF pronto para impressão no tamanho oficial CR80 (85,6 × 53,98 mm).
            </p>
            <Button onClick={() => navigate("/carteirinha")}
              variant="outline" className="mt-3 rounded-full w-full"
              data-testid="profile-goto-carteirinha-btn">
              Ir para carteirinha <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="surface p-6 sm:p-8 space-y-8">
          <div>
            <h2 className="font-heading text-lg font-bold">Dados do responsável</h2>
            <div className="grid gap-4 mt-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="p-name">Nome completo</Label>
                <Input id="p-name" value={form.name} onChange={upd("name")} data-testid="profile-input-name" />
              </div>
              <div>
                <Label htmlFor="p-email">E-mail</Label>
                <Input id="p-email" value={user.email} disabled data-testid="profile-input-email" />
              </div>
              <div>
                <Label htmlFor="p-phone">WhatsApp</Label>
                <Input id="p-phone" value={form.phone} onChange={upd("phone")} placeholder="(11) 90000-0000" data-testid="profile-input-phone" />
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.state || undefined} onValueChange={(v) => setForm((p) => ({ ...p, state: v }))}>
                  <SelectTrigger data-testid="profile-select-state"><SelectValue placeholder="Selecione seu estado" /></SelectTrigger>
                  <SelectContent>
                    {BRAZIL_STATES.map((u) => (<SelectItem key={u.uf} value={u.uf}>{u.uf} — {u.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                {region && <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Região: {region}</div>}
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="p-city">Cidade</Label>
                <Input id="p-city" value={form.city} onChange={upd("city")} data-testid="profile-input-city" />
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-lg font-bold">Dados da criança</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Utilizados na carteirinha e para acolhimento personalizado.</p>
            <div className="grid gap-4 mt-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="p-cname">Nome da criança</Label>
                <Input id="p-cname" value={form.child_name} onChange={upd("child_name")} data-testid="profile-input-child-name" />
              </div>
              <div>
                <Label htmlFor="p-cdob">Data de nascimento</Label>
                <Input id="p-cdob" type="date" value={form.child_birth_date || ""} onChange={upd("child_birth_date")} data-testid="profile-input-child-dob" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="p-ccond">Situação neuroatípica</Label>
                <Textarea
                  id="p-ccond"
                  rows={2}
                  value={form.child_condition}
                  onChange={upd("child_condition")}
                  placeholder="Ex.: Autismo (TEA), TDAH, Dislexia…"
                  data-testid="profile-input-child-condition"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {CONDITION_SUGGESTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm((p) => ({ ...p, child_condition: s }))}
                      className="text-xs px-2.5 py-1 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--primary))]/10 hover:border-[hsl(var(--primary))]/40 transition-colors"
                      data-testid={`profile-suggest-${s.slice(0,4).toLowerCase()}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                  Escrevemos com carinho: se você digitar com erro (ex.: &quot;autizmo&quot;), corrigimos automaticamente para o nome oficial.
                </p>
              </div>
              <div>
                <Label>Tipo de documento</Label>
                <Select value={form.document_type || undefined} onValueChange={(v) => setForm((p) => ({ ...p, document_type: v }))}>
                  <SelectTrigger data-testid="profile-select-doctype"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((d) => (<SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="p-docn">Número do documento</Label>
                <Input id="p-docn" value={form.document_number} onChange={upd("document_number")} data-testid="profile-input-docnumber" />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-[hsl(var(--border))]">
            <Button onClick={save} disabled={saving}
              className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 px-6"
              data-testid="profile-save-btn">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar alterações"}
            </Button>
          </div>
        </div>
      </section>

      <section className="container-cuidar pb-14">
        <div className="surface p-6 sm:p-8">
          <div className="mb-4">
            <h2 className="font-heading text-lg font-bold">Perfil de Apoiador(a)</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Ative para se cadastrar como voluntário(a) e/ou doador(a). Pessoa física ou empresa.
            </p>
          </div>
          <SupporterFields value={supporter} onChange={setSupporter} />
          <div className="mt-6 flex justify-end pt-4 border-t border-[hsl(var(--border))]">
            <Button onClick={save} disabled={saving}
              className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 px-6"
              data-testid="profile-save-supporter-btn">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar apoio"}
            </Button>
          </div>
        </div>
      </section>

      <DangerZone user={user} logout={logout} navigate={navigate} />

      <ImageCropper
        open={cropOpen}
        file={pendingFile}
        onCancel={() => { setCropOpen(false); setPendingFile(null); }}
        onCropped={onCropped}
        title="Ajustar sua foto"
      />
    </div>
  );
}


// Foto oficial para a carteirinha (separada da foto de perfil).
function MemberCardPhotoCard() {
  const { user, refresh } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const inputRef = useRef(null);
  const photoUrl = user?.member_card_photo_path ? `${API}/files/${user.member_card_photo_path}` : null;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Envie uma imagem."); return; }
    setPending(f);
    setCropOpen(true);
  };
  const onCropped = async (blob) => {
    setCropOpen(false); setPending(null);
    if (!blob) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", new File([blob], "carteirinha.jpg", { type: "image/jpeg" }));
      await api.post("/me/member-card-photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto da carteirinha salva.");
      await refresh();
    } catch (_) { toast.error("Falha ao enviar foto."); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remover a foto oficial da carteirinha?")) return;
    try { await api.delete("/me/member-card-photo"); toast.success("Foto removida."); await refresh(); }
    catch (_) { toast.error("Falha ao remover."); }
  };

  return (
    <div className="surface p-6" data-testid="member-card-photo-card">
      <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Foto para a Carteirinha</div>
      <div className="mt-4 flex flex-col items-center gap-3">
        <div className="w-28 h-36 rounded-md overflow-hidden border-2 border-white shadow-lg bg-[hsl(var(--muted))] grid place-items-center">
          {photoUrl
            ? <img src={photoUrl} alt="Foto carteirinha" className="w-full h-full object-cover" />
            : <div className="text-xs text-[hsl(var(--muted-foreground))] text-center px-3">Sem foto oficial</div>}
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="member-card-photo-input" />
        <div className="flex gap-2 w-full">
          <Button onClick={() => inputRef.current?.click()} disabled={uploading}
            className="flex-1 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
            data-testid="member-card-photo-change-btn">
            <Camera className="h-4 w-4 mr-1.5" /> {uploading ? "Enviando…" : (photoUrl ? "Trocar foto" : "Enviar foto")}
          </Button>
          {photoUrl && (
            <Button variant="outline" onClick={remove}
              className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10"
              data-testid="member-card-photo-remove-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/40 rounded-lg p-3 leading-relaxed">
          <div className="font-semibold text-[hsl(var(--foreground))] mb-1">Como tirar a foto ideal:</div>
          <ul className="space-y-0.5 list-disc list-inside">
            <li>Fundo branco e limpo (parede lisa serve)</li>
            <li>Boa iluminação, sem sombras no rosto</li>
            <li>Rosto centralizado, ombros à mostra (estilo 3x4)</li>
            <li>Sem óculos escuros, boné ou filtro</li>
          </ul>
        </div>
      </div>
      <ImageCropper
        open={cropOpen}
        file={pending}
        onCancel={() => { setCropOpen(false); setPending(null); }}
        onCropped={onCropped}
        title="Ajustar foto da carteirinha"
        aspect={3/4}
      />
    </div>
  );
}


// Excluir conta — LGPD/GDPR + exigência Apple Store.
// Requer senha atual + digitação "EXCLUIR" pra evitar delete acidental.
function DangerZone({ user, logout, navigate }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [waLink, setWaLink] = useState("");

  useEffect(() => {
    api.get("/config/whatsapp").then(({ data }) => {
      if (data?.number) {
        const txt = encodeURIComponent(`Olá! Quero solicitar a exclusão da minha conta (${user?.email}).`);
        setWaLink(`https://wa.me/${data.number}?text=${txt}`);
      }
    }).catch(() => {});
  }, [user]);

  const isAdmin = user?.role === "admin";

  const confirmDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "EXCLUIR") {
      toast.error('Digite "EXCLUIR" para confirmar.');
      return;
    }
    if (!password) {
      toast.error("Informe sua senha.");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/me", { data: { password, confirm: confirmText } });
      toast.success("Sua conta foi excluída. Sentiremos sua falta 💚");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir conta.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="container-cuidar pb-16">
      <div className="surface p-6 sm:p-8 border-[hsl(var(--destructive))]/30" data-testid="danger-zone-card">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg font-bold text-[hsl(var(--destructive))]">Zona de risco — Excluir minha conta</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Em cumprimento à LGPD e às diretrizes da Apple, você pode excluir sua conta
              a qualquer momento. A exclusão é <b>definitiva</b> e apaga: seu perfil, suas
              postagens na comunidade, comentários, fotos e histórico. Doações mensais
              precisam ser canceladas separadamente pelo portal do Mercado Pago.
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              {!isAdmin && (
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline"
                      className="rounded-full text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10"
                      data-testid="danger-delete-account-btn">
                      <Trash2 className="h-4 w-4 mr-1.5" /> Excluir minha conta
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-[hsl(var(--destructive))] flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" /> Excluir conta definitivamente
                      </DialogTitle>
                      <DialogDescription>
                        Essa ação é irreversível. Todos os seus dados (perfil, posts,
                        comentários, fotos) serão apagados. Se tiver uma doação recorrente
                        ativa, cancele pelo portal do Mercado Pago antes.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                      <div>
                        <Label htmlFor="del-pass">Sua senha</Label>
                        <Input id="del-pass" type="password" value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Digite sua senha atual"
                          data-testid="danger-password-input"
                          autoComplete="current-password" />
                      </div>
                      <div>
                        <Label htmlFor="del-confirm">Digite <b>EXCLUIR</b> para confirmar</Label>
                        <Input id="del-confirm" value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="EXCLUIR"
                          data-testid="danger-confirm-input" />
                      </div>
                    </div>
                    <DialogFooter className="mt-4">
                      <Button variant="ghost" onClick={() => setOpen(false)} data-testid="danger-cancel-btn">
                        Cancelar
                      </Button>
                      <Button
                        onClick={confirmDelete}
                        disabled={deleting || confirmText.trim().toUpperCase() !== "EXCLUIR" || !password}
                        className="rounded-full bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90"
                        data-testid="danger-confirm-btn">
                        <Trash2 className="h-4 w-4 mr-1.5" /> {deleting ? "Excluindo…" : "Excluir conta"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              {waLink && (
                <Button asChild variant="outline" className="rounded-full">
                  <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="danger-request-whatsapp">
                    <MessageCircle className="h-4 w-4 mr-1.5" /> Prefiro pedir pelo WhatsApp
                  </a>
                </Button>
              )}
            </div>

            {isAdmin && (
              <div className="mt-4 rounded-2xl bg-[hsl(var(--muted))]/70 px-4 py-3 text-xs text-[hsl(var(--muted-foreground))]">
                Contas de administrador só podem ser excluídas pelo suporte. Fale com a equipe pelo WhatsApp acima.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
